import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSquarespaceContent } from "../hooks/useSquarespaceContent";

const Header = ({ activeSection }) => {
  const { content } = useSquarespaceContent();
  const [isScrolled, setIsScrolled] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const shouldShrink = currentScrollY > 10;
      
      setIsScrolled(shouldShrink);
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // URL rewriting functionality from injection code
  useEffect(() => {
    if (!isHome) {
      document.querySelectorAll('a[href^="#"]').forEach(link => {
        const hash = link.getAttribute("href");
        // Avoid double prefixing if already rewritten
        if (!hash.startsWith("/#")) {
          link.setAttribute("href", "/" + hash); // converts #about → /#about
        }
      });
    }
  }, [isHome]);

  const getHeaderHeight = () => {
    const header = document.querySelector('.header');
    return header ? header.offsetHeight : 0;
  };

  const scrollToSection = (sectionId, isHomeSection = false) => {
    if (isHomeSection) {
      // If we're not on home page, navigate to home first
      if (!isHome) {
        navigate(`/#${sectionId}`);
        return;
      }
      
      // If we're on home page, scroll to section
      const element = document.getElementById(sectionId);
      let targetY = 0;
      
      if (element) {
        targetY = element.getBoundingClientRect().top + window.pageYOffset - getHeaderHeight() - 10;
      }
      
      if (sectionId === "home" && (!element || targetY < 10)) {
        targetY = 0;
      }
      
      window.scrollTo({ top: targetY, behavior: 'smooth' });
      
      // Update URL without page reload
      if (sectionId === "home") {
        window.history.pushState(null, '', '/');
      } else {
        window.history.pushState(null, '', `#${sectionId}`);
      }
    } else {
      // For non-home sections, just navigate
      navigate(`/#${sectionId}`);
    }
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    if (isHome) {
      scrollToSection("home", true);
    } else {
      navigate('/');
    }
  };

  const navItems = [
    { id: "about", label: "About", onClick: () => scrollToSection("about", true) },
    { id: "services", label: "Services", onClick: () => scrollToSection("services", true) },
    { id: "contact", label: "Contact", onClick: () => scrollToSection("contact", true) },
    { id: "news", label: "News", onClick: () => navigate('/news') },
  ];

  return (
    <header className={`header ${isScrolled ? "scrolled" : ""}`}>
      <nav className="nav">
        <div className="nav-brand">
          <a href="/" className="logo-link" onClick={handleLogoClick}>
            <img 
              src={isScrolled ? "/atsomnium-logo-blk.svg" : "/atsomnium-logo-wht.svg"}
              alt="ATSOMNIUM"
              className="logo"
              width="140"
              height="auto"
            />
          </a>
        </div>
        <ul className="nav-menu" role="navigation" aria-label="Main navigation">
          {navItems.map((item) => (
            <li key={item.id} className="nav-item">
              <button
                className={`nav-link ${activeSection === item.id ? "active" : ""}`}
                onClick={item.onClick}
                aria-current={activeSection === item.id ? "page" : undefined}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      </nav>
    </header>
  );
};

export default Header;